import ReactGA from 'react-ga4';
import {cloneDeep} from 'lodash';
import moment from 'moment';
import {DateTime, Interval, Duration} from 'luxon';
import {machineTypeMapping as _machineTypeMapping, MachineType as _MachineType} from '@ic/machine-type-constants';
import {
  taskTypeMapping as _taskTypeMapping,
  taskTypeDescriptions as _taskTypeDescriptions,
  TaskType as _TaskType,
  normalizeTaskType as _normalizeTaskType,
  resolveEffectiveTaskIds as _resolveEffectiveTaskIds,
  resolveEffectiveTaskNames as _resolveEffectiveTaskNames,
} from '@ic/machine-type-constants/Task_type';
// Shared with statsview-core so portal and Report Builder unit factors cannot drift.
import {
  KM_TO_MI,
  M_TO_FT,
  AC_TO_HA,
  HA_TO_AC,
  AC_TO_METERS2,
  HA_TO_METERS2,
  L_TO_GAL,
  GAL_TO_L,
  GASOLINE_TONNES_CO2E_PER_LITRE,
  DIESEL_TONNES_CO2E_PER_LITRE,
} from '@ic/statsview-core/units.js';
import {extractBeWhereSerialFromGeotabComment} from '@ic/statsview-core/installTypes.js';

export const machineTypeMapping = _machineTypeMapping;
export const MachineType = _MachineType;
export const taskTypeMapping = _taskTypeMapping;
export const taskTypeDescriptions = _taskTypeDescriptions;
export const TaskType = _TaskType;
export const normalizeTaskType = _normalizeTaskType;
export const resolveEffectiveTaskIds = _resolveEffectiveTaskIds;
export const resolveEffectiveTaskNames = _resolveEffectiveTaskNames;

/** Dropdown options; Other (0) listed last for UX while remaining the numeric default. */
export const taskTypeOptions = [
  {
    value: TaskType.GROUND_PREP,
    label: taskTypeMapping[TaskType.GROUND_PREP],
    description: taskTypeDescriptions[TaskType.GROUND_PREP],
  },
  {
    value: TaskType.PLANTING,
    label: taskTypeMapping[TaskType.PLANTING],
    description: taskTypeDescriptions[TaskType.PLANTING],
  },
  {value: TaskType.SPRAY, label: taskTypeMapping[TaskType.SPRAY], description: taskTypeDescriptions[TaskType.SPRAY]},
  {
    value: TaskType.CROP_MAINTENANCE,
    label: taskTypeMapping[TaskType.CROP_MAINTENANCE],
    description: taskTypeDescriptions[TaskType.CROP_MAINTENANCE],
  },
  {
    value: TaskType.IRRIGATION,
    label: taskTypeMapping[TaskType.IRRIGATION],
    description: taskTypeDescriptions[TaskType.IRRIGATION],
  },
  {
    value: TaskType.HARVEST,
    label: taskTypeMapping[TaskType.HARVEST],
    description: taskTypeDescriptions[TaskType.HARVEST],
  },
  {value: TaskType.OTHER, label: taskTypeMapping[TaskType.OTHER], description: taskTypeDescriptions[TaskType.OTHER]},
];

export const MS_PER_VEHICLEDAY = 10;

export {
  KM_TO_MI,
  M_TO_FT,
  AC_TO_HA,
  HA_TO_AC,
  AC_TO_METERS2,
  HA_TO_METERS2,
  L_TO_GAL,
  GAL_TO_L,
  GASOLINE_TONNES_CO2E_PER_LITRE,
  DIESEL_TONNES_CO2E_PER_LITRE,
};
export const PATH_VEHICLE_DAYS_MAP_RENDER_THRESHOLD = 5000;
// Sources:
// - CO₂: EIA - CO₂ Emissions Coefficients by Fuel (https://www.eia.gov/environment/emissions/co2_vol_mass.php)
// - CH₄/N₂O: EPA Emission Factors Hub Jan 2025 (https://www.epa.gov/system/files/documents/2025-01/ghg-emission-factors-hub-2025.pdf)
// - GWP: GHG Protocol Aug 2024 (https://ghgprotocol.org/sites/default/files/2024-08/Global-Warming-Potential-Values%20(August%202024).pdf)

export const SPANISH_DATE_ABBREVIATION = {
  'daysOfWeek': ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sá'],
  'monthNames': ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
};

export function numberWithCommas(x) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * Round a number to a specified number of decimal places, returns a number type,
 * works for floats cause javascript is weird & annoying like that
 *
 * @param {Number} value Value to round
 * @param {Number} decimalPlaces Number of decimal places to round to
 * @return {Number} Rounded value
 */
export function floatRound(value, decimalPlaces) {
  // Using Number.EPSILON to avoid floating point errors
  if (decimalPlaces > 0) {
    return Math.round((value + Number.EPSILON) * (10 * decimalPlaces)) / (10 * decimalPlaces);
  } else {
    Math.round(value);
  }
}

export function hasDuplicates(arr) {
  const noDupsArr = [];
  let foundDup = false;
  debugger;
  for (let i = 0; i < arr.length; i++) {
    if (noDupsArr.includes(arr[i])) {
      foundDup = true;
      break;
    } else {
      noDupsArr.push(arr[i]);
    }
  }
  return foundDup;
}

export function safeDivide(numerator, denominator) {
  return denominator === 0 ? 0 : numerator / denominator;
}

export function capitalizeFirstLetter(string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export async function fetchPostAuthSafe(url, options, sessionSyncUserName, sessionSyncDb, cache = null) {
  options.headers = options.headers || {};
  // If we make a post request to the backend and the sessionSyncDb or
  // sessionSyncUserName is null or not matching the request will be rejected,
  // by the requireLogin middleware and you will be logged out and an error will be displayed.
  // Guard here so a missing/empty client value never reaches the server (which would
  // serialize undefined as the string "undefined" and force a logout). The caller sees a
  // non-ok response and can recover instead of being logged out.
  if (!sessionSyncUserName || !sessionSyncDb) {
    console.error('[fetchPostAuthSafe] Refusing POST with missing session sync values (would force logout):', {
      url,
      sessionSyncUserName,
      sessionSyncDb,
    });
    return new Response(JSON.stringify({error: 'Session sync values unavailable', resyncNeeded: true}), {
      status: 409,
      headers: {'Content-Type': 'application/json'},
    });
  }
  options.headers['sessionSyncDb'] = sessionSyncDb;
  options.headers['sessionSyncUserName'] = sessionSyncUserName;
  const result = await fetch(url, options, cache);
  await reloadIfCustomerChangedElsewhere(result);
  return result;
}

/**
 * A 409 carrying reloadRequired means the active customer was switched in another tab, so this
 * page is looking at the previous customer. Reload rather than let the caller act on stale data.
 *
 * @param {Response} response - response to inspect, consumed via a clone so the caller still reads it
 * @return {Promise<void>}
 */
async function reloadIfCustomerChangedElsewhere(response) {
  if (!response || response.status !== 409) {
    return;
  }

  try {
    const body = await response.clone().json();
    if (body && body.reloadRequired && typeof window !== 'undefined') {
      console.warn('[fetchPostAuthSafe] Active customer changed in another tab - reloading');
      window.location.reload();
    }
  } catch (error) {
    // A 409 without a JSON body is not ours to act on
  }
}

export async function fetchPostAuthSafeWithTimeout(
  url,
  options,
  sessionSyncUserName,
  sessionSyncDb,
  timeout = 10000,
  cache = null
) {
  options.headers = options.headers || {};

  // Guard against missing sync values which would force a server-side logout.
  if (!sessionSyncUserName || !sessionSyncDb) {
    console.error('[fetchPostAuthSafeWithTimeout] Refusing POST with missing session sync values:', {
      url,
      sessionSyncUserName,
      sessionSyncDb,
    });
    return {requestSuccess: false};
  }

  options.headers['sessionSyncDb'] = sessionSyncDb;
  options.headers['sessionSyncUserName'] = sessionSyncUserName;

  // Check if online
  if (!navigator.onLine) {
    return {requestSuccess: false};
  }

  // Create timeout signal
  options.signal = AbortSignal.timeout(timeout);

  // Attempt request
  let result;
  try {
    result = await fetch(url, options, cache);
  } catch (err) {
    return {requestSuccess: false};
  }

  await reloadIfCustomerChangedElsewhere(result);

  return {requestSuccess: true, result: result};
}

export function generateGeoFencesDict(geoFences) {
  const geoFencesDict = {
    'fieldMapping': {},
    'regionMapping': {},
    '-1': {
      'rowPassEnabled': false,
    },
  };
  geoFences.forEach((geoFence) => {
    geoFencesDict[geoFence.properties.intelliblock_num.toString()] = geoFence.properties;

    // Add mapping of regions/fields to their child blocks
    if (!Object.prototype.hasOwnProperty.call(geoFencesDict['fieldMapping'], geoFence.properties.field_id)) {
      geoFencesDict['fieldMapping'][geoFence.properties.field_id] = [];
    }
    geoFencesDict['fieldMapping'][geoFence.properties.field_id].push({
      block_name: geoFence.properties.block_name,
      block_id: geoFence.properties.block_id,
      intelliblock_num: geoFence.properties.intelliblock_num,
    });

    if (!Object.prototype.hasOwnProperty.call(geoFencesDict['regionMapping'], geoFence.properties.region_id)) {
      geoFencesDict['regionMapping'][geoFence.properties.region_id] = [];
    }
    geoFencesDict['regionMapping'][geoFence.properties.region_id].push({
      block_name: geoFence.properties.block_name,
      block_id: geoFence.properties.block_id,
      intelliblock_num: geoFence.properties.intelliblock_num,
    });
  });

  return geoFencesDict;
}

export function searchFind(item, search) {
  if (search.length < 2) {
    // Minimum search length
    return true;
  }
  if (Array.isArray(item.props.children)) {
    for (let i = 0; i < item.props.children.length; i++) {
      if (typeof item.props.children[i] === 'object') {
        if (searchFind(item.props.children[i], search)) {
          return true;
        }
      }
    }
  } else if (typeof item.props.children === 'object') {
    return searchFind(item.props.children, search);
  } else if (typeof item.props.children === 'string') {
    return item.props.children.trim().toLowerCase().includes(search);
  } else if (item.props.hasOwnProperty('dangerouslySetInnerHTML')) {
    // Change script to not use dangerouslySetInnerHTML
    return (
      item.props.dangerouslySetInnerHTML.__html.trim().toLowerCase().includes(search) &&
      !(search.includes('<') || search.includes('>')) &&
      !(item.props.dangerouslySetInnerHTML.__html.trim().toLowerCase().includes('<br>') && search.includes('br'))
    );
  }
  return false;
}

export function arrayToggle(arr, item) {
  return arr.includes(item)
    ? arr.filter((i) => {
        return i !== item;
      })
    : [...arr, item];
}

export function sendGAEvent(eventName, itemName, contentType) {
  ReactGA.event(eventName, {
    category: 'engagement',
    label: itemName,
    content_type: contentType,
  });
}

export function sendGAPageview(page, forceGAEnabled = false) {
  if (forceGAEnabled) {
    initGA();
  }
  ReactGA.send({hitType: 'pageview', page: page});
}

// Enable and Disable Google Analytics object. If disabled functions will still run, analytics won't be submitted
export function initGA() {
  if (window.location.host == 'icserver.co') {
    ReactGA.initialize('G-1MMZ0GHQPW');
  } else {
    ReactGA.initialize('G-Y4M9FWF6SP');
  }
}

export function disableGA() {
  ReactGA.reset();
}

// Returns value units in meters
export function unitsLengthSubmitConversion(value, inputUnits) {
  // Returns value in km or m. inputUnits can be 'km', 'mi', 'm', 'ft'
  if (value == '') {
    return value;
  } else if (inputUnits == 'mi' || inputUnits == 'mph') {
    return value / KM_TO_MI;
  } else if (inputUnits == 'km' || inputUnits == 'kph') {
    return value;
  } else if (inputUnits == 'ft') {
    return parseFloat((value / M_TO_FT).toFixed(6));
  } else if (inputUnits == 'm') {
    return value;
  } else {
    console.error('inputUnits can be either "km", "mi", "m", "ft", "mph", "kph"');
  }
}

// Return value units specified in outputUnits
export function unitsLengthDisplayConversion(value, outputUnits) {
  // Returns value in OutputUnits. OutputUnits can be 'km', 'mi', 'm', 'ft'
  if (value == '') {
    return value;
  } else if (outputUnits == 'mi' || outputUnits == 'mph') {
    return parseFloat(value * KM_TO_MI);
  } else if (outputUnits == 'km' || outputUnits == 'kph') {
    return parseFloat(value);
  } else if (outputUnits == 'ft') {
    return parseFloat(value * M_TO_FT);
  } else if (outputUnits == 'm') {
    return parseFloat(value);
  } else {
    console.error('outputUnits can be either "km", "mi", "m", "ft", "mph", "kph"');
  }
}

export function generateRandomId(length) {
  let id = '';
  const characters = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  for (let i = 0; i < length; i++) {
    id += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return id;
}

/**
 * Function to convert area units
 *
 * @param {Number} value value to be converted
 * @param {String} outputUnits either 'ha', 'ac', 'meters2'
 * @param {String} inputUnits either 'ha', 'ac', 'meters2'
 * @return {Number} converted value
 */
export function unitsAreaConversion(value, outputUnits, inputUnits = 'meters2') {
  // Check if value is valid
  if (value == '' || value == null || value == undefined || typeof value != 'number') {
    return value;
  }

  // Check if outputUnits is valid
  if (!(outputUnits == 'ha' || outputUnits == 'ac' || outputUnits == 'meters2')) {
    console.error('unitsAreaConversion: output units are not valid, can only be "ha", "ac", "meters2"');
    return value;
  }

  // convert ha input
  if (inputUnits == 'ha') {
    if (outputUnits == 'ac') {
      return value * HA_TO_AC;
    } else if (outputUnits == 'meters2') {
      return value * HA_TO_METERS2;
    } else {
      return value; // already in ha
    }
  }

  // convert ac input
  else if (inputUnits == 'ac') {
    if (outputUnits == 'ha') {
      return value * AC_TO_HA;
    } else if (outputUnits == 'meters2') {
      return value * AC_TO_METERS2;
    } else {
      return value; // already in ac
    }
  }

  // convert meters2 input
  else if (inputUnits == 'meters2') {
    if (outputUnits == 'ha') {
      return value / HA_TO_METERS2;
    } else if (outputUnits == 'ac') {
      return value / AC_TO_METERS2;
    } else {
      return value; // already in meters2
    }
  } else {
    console.error('invalid inputUnits, can be "ha", "ac", "meters2"');
  }
}

export function formatTime(time) {
  let hours = Math.floor(time / 60);
  let min = Math.round(time - hours * 60);

  if (min == 60) {
    hours += 1;
    min = 0;
  }

  if (time >= 60 || hours >= 1) {
    return hours + 'h ' + min + 'm';
  } else if (time > 0) {
    return min + 'm';
  } else {
    return 0 + 'm';
  }
}

export function distanceBetweenPoints(lng, lat, lngPrev, latPrev) {
  // Calculate distance in meters between points for separating trips and bearing
  lng = parseFloat(lng);
  lat = parseFloat(lat);
  lngPrev = parseFloat(lngPrev);
  latPrev = parseFloat(latPrev);

  const x = latPrev * (Math.PI / 180);
  const y = lat * (Math.PI / 180);

  const dlat = (lat - latPrev) * (Math.PI / 180);
  const dlng = (lng - lngPrev) * (Math.PI / 180);
  const a = Math.pow(Math.sin(dlat / 2), 2) + Math.cos(x) * Math.cos(y) * Math.pow(Math.sin(dlng / 2), 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const dist = c * 6371 * 1000;
  return dist;
}

export function stringToArray(string, delimeter = ',') {
  const array = string.split(delimeter);
  const trimed = array.map((item) => {
    return item.trim();
  });
  const filtered = trimed.filter(Boolean);
  return filtered;
}

export const rowApplicationTypeMapping = {
  0: 'Width',
  1: 'Limited',
  2: 'Rows',
};

export const roleValueToDisplayNameMapping = {
  'ic_admin': 'IC Admin',
  'customer_admin': 'Admin',
  'user': 'User',
  'inspection_only': 'Operator (name only, no login)',
};

// IC-5874 static Job Role list, stored as this exact text in databaseAccess[db].jobRole
// Keep the backend copy in modules/Utils.js in sync
export const USER_JOB_ROLES = [
  'Mechanic',
  'Shop Manager',
  'Operator',
  'Foreman',
  'Supervisor',
  'PCA',
  'Farm Manager',
  'Office Staff',
  'Finance',
  'Director',
  'Executive',
  'Other',
]
  // Alphabetical for display, 'Other' always last
  .sort((a, b) => {
    if (a === 'Other' || b === 'Other') {
      return a === 'Other' ? 1 : -1;
    }
    return a.localeCompare(b);
  });

// Case-insensitive match to the static list, returns canonical text, '' for empty, null when invalid
export function normalizeUserJobRole(rawValue) {
  const trimmed = String(rawValue ?? '').trim();
  if (trimmed === '') {
    return '';
  }
  const match = USER_JOB_ROLES.find((jobRole) => {
    return jobRole.toLowerCase() === trimmed.toLowerCase();
  });
  return match ?? null;
}

/**
 * Resolves how the inspection form should collect the inspector name for the current user.
 *
 * @param {Object} customerSettings Customer settings (expects inspection.inspectionNameInputType: 'text'|'dropdown'|null)
 * @param {Object} userSettings User settings (expects inspectionDropDownNameInputEnabled under .general)
 * @return {'text'|'dropdown'|'none'}
 */
export function getInspectionNameInputType(customerSettings, userSettings) {
  const customerType = customerSettings?.inspection?.inspectionNameInputType;
  if (customerType === 'text') {
    return 'text';
  }
  if (customerType === 'dropdown' && userSettings?.general?.inspectionDropDownNameInputEnabled) {
    return 'dropdown';
  }
  return 'none';
}

export const languageValueToDisplayNameMapping = {
  'en': 'English',
  'es': 'Spanish',
};

export const accessLevels = Object.freeze({
  user: 0,
  customer_admin: 1,
  ic_admin: 2,
});

/**
 * Whether Cropview should show the REC / Work Order column for this customer.
 * Active when any rec integration source is enabled (Agrian, AgCode, Agworld).
 * @param {object|undefined} customerSettings Redux customer settings payload.
 * @return {boolean}
 */
export function isCropviewRecColumnEnabled(customerSettings) {
  const general = customerSettings?.general || {};
  return !!general.enableRecApplication || !!general.agcodeEnabled || !!general.agworldEnabled;
}

/**
 * Column header for the rec column — AgCode and Agworld surface work orders;
 * Agrian surfaces REC IDs.
 * @param {object|undefined} customerSettings Redux customer settings payload.
 * @return {string}
 */
export function getCropviewRecColumnLabel(customerSettings) {
  const general = customerSettings?.general || {};
  if (general.agcodeEnabled || general.agworldEnabled) {
    return 'Work Order';
  }
  return 'REC';
}

/**
 * CSV export header fragment for the rec / work-order column.
 * @param {object|undefined} customerSettings Redux customer settings payload.
 * @return {string}
 */
export function getCropviewRecExportHeaderLabel(customerSettings) {
  const general = customerSettings?.general || {};
  if (general.agcodeEnabled || general.agworldEnabled) {
    return 'Work Order,';
  }
  return 'Rec ID,';
}

/**
 * Default rec_event source when clearing a rec (no recObj on the row). Mirrors
 * CropviewRoutes rec-list precedence: AgCode > Agrian > Agworld.
 * @param {object|undefined} customerSettings Redux customer settings payload.
 * @return {string}
 */
export function getCropviewRecEventSource(customerSettings) {
  const general = customerSettings?.general || {};
  if (general.agcodeEnabled) {
    return 'agcode';
  }
  if (general.agrianAccountName) {
    return 'agrian';
  }
  if (general.agworldEnabled) {
    return 'agworld';
  }
  return '';
}

/**
 * Whether this customer uses a work-order style integration (AgCode or Agworld).
 * @param {object|undefined} customerSettings Redux customer settings payload.
 * @return {boolean}
 */
export function isCropviewWorkOrderIntegrationEnabled(customerSettings) {
  const general = customerSettings?.general || {};
  return !!general.agcodeEnabled || !!general.agworldEnabled;
}

export const modalBoxStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: '60%',
  width: {
    xs: '90%',
    sm: '60%',
  },
  maxHeight: '100%',
  height: '90vh',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 2,
  overflow: 'scroll',
};

export const hideCell = {
  display: {
    xs: 'none',
    sm: 'revert',
  },
};

export const squareButton = {
  minWidth: 0,
  paddingLeft: 1.8,
  paddingRight: 1.8,
  paddingTop: 1.2,
  paddingBottom: 1.2,
};

export const customTheme = {
  components: {
    MuiButton: {
      variants: [
        {
          props: {variant: 'ic-button', color: 'primary'},
          style: {
            'textTransform': 'none',
            'color': '#fff',
            'backgroundColor': '#4e73df',
            'borderColor': '#4e73df',
            '&:hover': {
              'backgroundColor': '#2e59d9',
              'borderColor': '#2653d4',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(105, 136, 228, .5)`,
              'outline': '0',
            },
          },
        },
        {
          props: {variant: 'ic-button', color: 'primary-bordered'},
          style: {
            'textTransform': 'none',
            'color': '#fff',
            'backgroundColor': '#4e73df',
            'border': '1px solid #000',
            '&:hover': {
              'backgroundColor': '#2e59d9',
              'border': '1px solid #111',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(105, 136, 228, .5)`,
              'outline': '0',
            },
          },
        },
        {
          props: {variant: 'ic-button', color: 'secondary'},
          style: {
            'textTransform': 'none',
            'color': '#fff',
            'backgroundColor': '#858796',
            'borderColor': '#858796',
            '&:hover': {
              'backgroundColor': '#717384',
              'borderColor': '#6b6d7d',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(151, 153, 166, .5)`,
              'outline': '0',
            },
          },
        },
        {
          props: {variant: 'ic-button', color: 'danger'},
          style: {
            'textTransform': 'none',
            'color': '#fff',
            'backgroundColor': '#e74a3b',
            'borderColor': '#e74a3b',
            '&:hover': {
              'backgroundColor': '#e02d1b',
              'borderColor': '#d52a1a',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(235, 101, 88, .5)`,
              'outline': '0',
            },
          },
        },
        {
          props: {variant: 'ic-button', color: 'success'},
          style: {
            'textTransform': 'none',
            'color': '#fff',
            'backgroundColor': '#1cc88a',
            'borderColor': '#1cc88a',
            '&:hover': {
              'backgroundColor': '#218838',
              'borderColor': '#1e7e34',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(78, 115, 223, .25)`,
              'outline': '0',
            },
          },
        },
        {
          props: {variant: 'ic-button', color: 'light'},
          style: {
            'color': '#3a3b45',
            'minWidth': '40px',
            'backgroundColor': 'transparent',
            'borderWidth': '1px',
            'borderStyle': 'solid',
            'borderColor': '#3a3b45',
            '&:hover': {
              'backgroundColor': '#d3d3d3',
            },
            '&:focus': {
              'boxShadow': `0 0 0 0.2rem rgba(151, 153, 166, .5)`,
              'outline': '0',
            },
          },
        },
      ],
    },
  },
  typography: {
    fontFamily:
      '-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,Oxygen,Ubuntu,Cantarell,Fira Sans,Droid Sans,Helvetica Neue,sans-serif',
  },
};

export const intRegex = /^[0-9]*$/;
export const floatRegex = /^(\d*([.]\d*)?|[.]\d+)$/;
export const dollarRegex = /^(\d+([.]\d{0,2})?|[.]\d{0,2})$/;
/** Allows empty or numeric input with optional decimal and up to 2 decimal places (e.g. for salary $/hr). */
export const salaryInputRegex = /^\d*\.?\d{0,2}$/;
export const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

export const decimalLimitRegex = (decimalLimit) => {
  let re;
  if (decimalLimit < 1) {
    re = /^[0-9]*$/;
  } else {
    re = new RegExp(String.raw`^(\d+([.]\d{0,${decimalLimit}})?|[.]\d{0,${decimalLimit}})$`);
  }

  return re;
};

/** Matches Chart.js `defaults.font.family` so React mirrors beside the canvas look the same. */
export const chartJsFontFamily = "'Helvetica Neue', 'Helvetica', 'Arial', sans-serif";

export const customChartProperties = {
  titleFontSize: 18,
  legendFontSize: 14,
  axisTitleFontSize: 12,
  axisTitleFontWeight: 600,
  xLabelFontSize: 10,
  lineHeight: 0.5,
  autoSkipLimit: 20,
  colorGreen: '#00e676',
  colorYellow: '#ffc107',
  colorBlue: '#2979ff',
  colorRed: '#ff5252',
  colorGrey: '#c4c4c4',
  colorLightGrey: '#D3D3D3',
  colorClear: '#F4FAFC',
  colorLimeGreen: '#00ff00',
  colorOffYellow: '#fff68f',
  colorWarning: '#F6C23E',
  colorDanger: '#E74A3B',
  colorOrange: '#ff8000',
  colorBlack: '#000000',
  colorWhite: '#ffffff',
};

export function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export function deepCopy(obj) {
  if (typeof obj !== 'object' || obj === null) {
    return obj;
  }

  if (Array.isArray(obj)) {
    const newArray = [];
    for (const item of obj) {
      newArray.push(deepCopy(item));
    }
    return newArray;
  }

  const newObj = {};
  for (const key in obj) {
    if (obj.hasOwnProperty(key)) {
      newObj[key] = deepCopy(obj[key]);
    }
  }
  return newObj;
}

// Input value is in L/hr. unitsLength is 'km' or 'mi' (also accepts 'kph'|'mph').
// Returns { value, unit } where value is numeric in the chosen display unit.
export function convertFuelFromLength(value, unitsLength) {
  if (value === '' || value == null) return {value, unit: 'L/hr'};

  const v = Number(value);
  if (!Number.isFinite(v)) return {value, unit: 'L/hr'};

  const useGal = unitsLength === 'mi' || unitsLength === 'mph';
  return useGal ? {value: v * L_TO_GAL, unit: 'gal/hr'} : {value: v, unit: 'L/hr'};
}
// Rounded variant for display
export function convertFuelFromLengthRounded(value, unitsLength, decimals = 2) {
  const {value: v, unit} = convertFuelFromLength(value, unitsLength);
  const n = Number(v);
  return {
    value: Number.isFinite(n) ? Number(n.toFixed(decimals)) : v,
    unit,
  };
}
// Rounded variant for display
export function convertFuelFromLengthRoundedDisplay(value, unitsLength, decimals = 2) {
  const {value: v, unit} = convertFuelFromLength(value, unitsLength);
  const n = Number(v);
  return Number.isFinite(n) ? Number(n.toFixed(decimals)) : v;
}
// For display we are typically converting L to Gal when applicable
export function unitsFuelDisplayConversion(value, unitsLengthSystem) {
  if (value === '' || value == null) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return unitsLengthSystem === 'imperial' ? n * L_TO_GAL : n;
}
export function unitsFuelSubmitConversion(value, unitsLengthSystem) {
  if (value === '' || value == null) return value;
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  // User edits are in gal/hr when imperial. Store in L/hr.
  return unitsLengthSystem === 'imperial' ? n * GAL_TO_L : n;
}

export function formatString(inputString) {
  // Split the input string into an array of words
  const words = inputString.split('_');

  // Capitalize each word
  const capitalizedWords = words.map(function (word) {
    return word.charAt(0).toUpperCase() + word.slice(1);
  });

  // Join the words back into a single string
  const formattedString = capitalizedWords.join(' ');

  return formattedString;
}

export function sortVehicleNamesHelper(vehicleAName, vehicleBName) {
  // Sort serial numbers to back
  const vehicleAIsDeviceSerial =
    ['G9', 'GA'].includes(vehicleAName.slice(0, 2)) && vehicleAName.length == 12 && /^[a-zA-Z0-9]+$/.test(vehicleAName);
  const vehicleBIsDeviceSerial =
    ['G9', 'GA'].includes(vehicleBName.slice(0, 2)) && vehicleBName.length == 12 && /^[a-zA-Z0-9]+$/.test(vehicleBName);

  if (vehicleAIsDeviceSerial && !vehicleBIsDeviceSerial) {
    return 1;
  } else if (!vehicleAIsDeviceSerial && vehicleBIsDeviceSerial) {
    return -1;
  } else {
    return vehicleAName.localeCompare(vehicleBName);
  }
}

export function trimObjectStrings(inputObj) {
  const obj = cloneDeep(inputObj);

  if (typeof obj === 'object' && obj !== null) {
    for (const key in obj) {
      if (typeof obj[key] === 'string') {
        obj[key] = obj[key].trim();
      }
    }
  }

  return obj;
}

export async function* streamAsyncIterator(stream) {
  // Get a lock on the stream
  const reader = stream.getReader();

  try {
    while (true) {
      // Read from the stream
      const {done, value} = await reader.read();
      // Exit if we're done
      if (done) return;
      // Else yield the chunk
      yield value;
    }
  } finally {
    reader.releaseLock();
  }
}

// Firefox and safari with input type 'number' do not prevent non-numbers from being entered,
// but also do not trigger an onChange event for non-numbers
// Use function in combination with input type 'text' to trigger onChange event for all characters
export function handleAllowOnlyNumbers(event) {
  const newKeyValue = event.target.value + event.key;
  if (event.key.length == 1 && !event.altKey && !event.ctrlKey && !newKeyValue.match(floatRegex)) {
    event.preventDefault();
  }
}

export function polygonArea(vertices) {
  let area = 0;
  for (let i = 0; i < vertices.length; i++) {
    const j = (i + 1) % vertices.length;
    area += vertices[i][0] * vertices[j][1];
    area -= vertices[j][0] * vertices[i][1];
  }
  return area / 2;
}

export function roundDecimalPlaces(inputValue, decimalPlaces) {
  let value = inputValue;
  if (typeof value == 'string') {
    value = parseFloat(value.replace(/,/g, ''));
  }

  const roundingval = Math.pow(10, decimalPlaces);
  return (Math.round(value * roundingval) / roundingval).toFixed(decimalPlaces);
}

// Global kill switch for the towing columns; each consumer also restricts them to ic_admin users
export const DISPLAY_TOWING_TIME = true;

export {
  GEOTAB_INSTALL_BESOL,
  GEOTAB_INSTALL_BATTERY_POWERED,
  extractBeWhereSerialFromGeotabComment,
  checkIfDeviceIsBesol,
  checkIfDeviceIsBatteryPowered,
  checkIfDeviceIsSelfPoweredGeotabInstall,
} from '@ic/statsview-core/installTypes.js';

export function unionLabelsAndDataZones(opsData, compareByOpsData, excludedKeys = []) {
  const opsDataCopy = deepCopy(opsData);
  const compareByOpsDataCopy = deepCopy(compareByOpsData);
  // Do for Vehicles
  const opsDataKeys = Object.keys(opsDataCopy);
  const compareByOpsDataKeys = Object.keys(compareByOpsDataCopy);
  opsDataKeys.forEach((opsDataKey) => {
    if (!compareByOpsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(opsDataCopy[opsDataKey]);
      Object.keys(objZeros).forEach((innerObjKey) => {
        if (!excludedKeys.includes(innerObjKey)) {
          objZeros[innerObjKey] = 0;
        }
      });
      compareByOpsDataCopy[opsDataKey] = objZeros;
    }
  });
  compareByOpsDataKeys.forEach((opsDataKey) => {
    if (!opsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(compareByOpsDataCopy[opsDataKey]);
      Object.keys(objZeros).forEach((innerObjKey) => {
        if (!excludedKeys.includes(innerObjKey)) {
          objZeros[innerObjKey] = 0;
        }
      });
      opsDataCopy[opsDataKey] = objZeros;
    }
  });

  return [opsDataCopy, compareByOpsDataCopy];
}

export function unionLabelsAndDataTasks(opsData, compareByOpsData, excludedKeys = []) {
  const opsDataCopy = deepCopy(opsData);
  const compareByOpsDataCopy = deepCopy(compareByOpsData);
  // Do for Vehicles
  const opsDataKeys = Object.keys(opsDataCopy);
  const compareByOpsDataKeys = Object.keys(compareByOpsDataCopy);

  opsDataKeys.forEach((opsDataKey) => {
    if (!compareByOpsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(opsDataCopy[opsDataKey]);
      // In this case innerObjKey is the zone key
      Object.keys(objZeros).forEach((innerObjKey) => {
        Object.keys(objZeros[innerObjKey]).forEach((secondInnerObjKey) => {
          Object.keys(objZeros[innerObjKey][secondInnerObjKey]).forEach((thirdInnerObjKey) => {
            objZeros[innerObjKey][secondInnerObjKey][thirdInnerObjKey] = 0;
          });
        });
      });
      compareByOpsDataCopy[opsDataKey] = objZeros;
    } else {
      const objZeros = deepCopy(opsDataCopy[opsDataKey]);
      const compareByObjZeros = deepCopy(compareByOpsDataCopy[opsDataKey]);
      Object.keys(objZeros).forEach((innerObjKey) => {
        // Second inner obj key is the zone name
        Object.keys(objZeros[innerObjKey]).forEach((secondInnerObjKey) => {
          // If the task exists on the compare by but not for this specific zone name
          if (!Object.keys(compareByObjZeros[innerObjKey]).includes(secondInnerObjKey)) {
            // We create that zone on this object and then set it all to 0
            compareByObjZeros[innerObjKey][secondInnerObjKey] = {};
            Object.keys(objZeros[innerObjKey][secondInnerObjKey]).forEach((thirdInnerObjKey) => {
              compareByObjZeros[innerObjKey][secondInnerObjKey][thirdInnerObjKey] = 0;
            });
          }
        });
        compareByOpsDataCopy[opsDataKey] = compareByObjZeros;
      });
    }
  });

  compareByOpsDataKeys.forEach((opsDataKey) => {
    if (!opsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(compareByOpsDataCopy[opsDataKey]);
      // In this case innerObjKey is the zone key
      Object.keys(objZeros).forEach((innerObjKey) => {
        Object.keys(objZeros[innerObjKey]).forEach((secondInnerObjKey) => {
          Object.keys(objZeros[innerObjKey][secondInnerObjKey]).forEach((thirdInnerObjKey) => {
            objZeros[innerObjKey][secondInnerObjKey][thirdInnerObjKey] = 0;
          });
        });
      });
      opsDataCopy[opsDataKey] = objZeros;
    } else {
      const objZeros = deepCopy(opsDataCopy[opsDataKey]);
      const compareByObjZeros = deepCopy(compareByOpsDataCopy[opsDataKey]);
      Object.keys(compareByObjZeros).forEach((innerObjKey) => {
        // Second inner obj key is the zone name
        Object.keys(compareByObjZeros[innerObjKey]).forEach((secondInnerObjKey) => {
          // If the task exists on the og set but not for this specific zone name
          if (!Object.keys(objZeros[innerObjKey]).includes(secondInnerObjKey)) {
            // We create that zone on this object and then set it all to 0
            objZeros[innerObjKey][secondInnerObjKey] = {};
            Object.keys(compareByObjZeros[innerObjKey][secondInnerObjKey]).forEach((thirdInnerObjKey) => {
              objZeros[innerObjKey][secondInnerObjKey][thirdInnerObjKey] = 0;
            });
          }
        });
        opsDataCopy[opsDataKey] = objZeros;
      });
    }
  });

  return [opsDataCopy, compareByOpsDataCopy];
}

export function unionLabelsAndDataVehicles(opsData, compareByOpsData, excludedKeys = []) {
  const opsDataCopy = deepCopy(opsData);
  const compareByOpsDataCopy = deepCopy(compareByOpsData);

  // Do for Vehicles
  const opsDataKeys = Object.keys(opsDataCopy);
  const compareByOpsDataKeys = Object.keys(compareByOpsDataCopy);

  opsDataKeys.forEach((opsDataKey) => {
    if (!compareByOpsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(opsDataCopy[opsDataKey]);
      Object.keys(objZeros).forEach((innerObjKey) => {
        if (!excludedKeys.includes(innerObjKey)) {
          objZeros[innerObjKey] = 0;
        }
      });
      compareByOpsDataCopy[opsDataKey] = objZeros;
    }
  });
  compareByOpsDataKeys.forEach((opsDataKey) => {
    if (!opsDataKeys.includes(opsDataKey)) {
      const objZeros = deepCopy(compareByOpsDataCopy[opsDataKey]);
      Object.keys(objZeros).forEach((innerObjKey) => {
        if (!excludedKeys.includes(innerObjKey)) {
          objZeros[innerObjKey] = 0;
        }
      });
      opsDataCopy[opsDataKey] = objZeros;
    }
  });

  return [opsDataCopy, compareByOpsDataCopy];
}

export function countDecimals(number) {
  if (Math.floor(number.valueOf()) === number.valueOf()) return 0;

  const str = number.toString();
  if (str.indexOf('.') !== -1 && str.indexOf('-') !== -1) {
    return str.split('-')[1] || 0;
  } else if (str.indexOf('.') !== -1) {
    return str.split('.')[1].length || 0;
  }
  return str.split('-')[1] || 0;
}

export function getUserDisplayName(userObj, userId, icAdminOverride = false) {
  if (userObj) {
    if (userObj.username.includes('_icadmin') && icAdminOverride) {
      return 'IntelliCulture Admin';
    }
    const userDisplayName = (userObj?.name || userObj.username) ?? '';
    return userDisplayName;
  }
  return userId;
}

/**
 * Resolve the display name for who performed an inspection. Matches the Statsview
 * precedence: operator (looked up by uid) -> entered inspection name -> inspecting
 * user's display name -> username.
 * @param {Object} report - row/report data with inspectedByOperator, inspectionNameInput, inspectedByUser
 * @param {Object} operatorsLookUp - map of operator uid -> {name}
 * @param {Object} databaseUserNames - map of user uid -> {name, username}
 * @return {string} the resolved inspected-by display name
 */
export function getInspectionInspectedByName(report, operatorsLookUp = {}, databaseUserNames = {}) {
  const operator = operatorsLookUp[report.inspectedByOperator];
  if (operator && operator.name) {
    return operator.name;
  }
  if (report.inspectionNameInput) {
    return report.inspectionNameInput;
  }
  return getUserDisplayName(databaseUserNames[report.inspectedByUser], report.inspectedByUser);
}

/**
 * Resolve the display name for who submitted an issue report.
 * Matches live breakdown alerts: entered/selected operator name, else submitter account.
 * @param {Object} report - breakdown report with operatorName / operator_name and submittedByUser / submitted_by_user
 * @param {Object} databaseUserNames - map of user uid -> {name, username}
 * @return {string} the resolved reported-by display name
 */
export function getBreakdownReportedByName(report, databaseUserNames = {}) {
  if (!report) {
    return '';
  }
  const operatorName = (report.operatorName || report.operator_name || '').trim();
  if (operatorName) {
    return operatorName;
  }
  const submittedByUser = report.submittedByUser || report.submitted_by_user;
  if (!submittedByUser) {
    return '';
  }
  const displayName = getUserDisplayName(databaseUserNames[submittedByUser], submittedByUser);
  return displayName === submittedByUser ? '' : displayName || '';
}

// Multi-org active-devices helpers. activeDevices[provider] is { connectionid: { deviceId: device } }
// because provider device ids (esp. Geotab `b1`-style) collide across databases. Resolve a device
// by its vehicle's connectionid; if the connectionid is missing (legacy row) or the device isn't
// under it, fall back to searching every connection (best-effort).
export function getActiveDevice(activeDevices, provider, connectionid, deviceId) {
  if (deviceId == null || deviceId === '') return undefined;
  const byConn = (activeDevices && activeDevices[provider]) || {};
  if (connectionid && byConn[connectionid] && byConn[connectionid][deviceId]) {
    return byConn[connectionid][deviceId];
  }
  for (const connMap of Object.values(byConn)) {
    if (connMap && connMap[deviceId]) return connMap[deviceId];
  }
  return undefined;
}

export function hasActiveDevice(activeDevices, provider, connectionid, deviceId) {
  return getActiveDevice(activeDevices, provider, connectionid, deviceId) !== undefined;
}

// Flatten a provider's connectionid-scoped device maps into a single array (each device keeps its
// own connectionid field). Use for dropdowns / listings across all connections.
export function flattenActiveDevices(activeDevices, provider) {
  const byConn = (activeDevices && activeDevices[provider]) || {};
  const out = [];
  for (const connMap of Object.values(byConn)) {
    for (const device of Object.values(connMap || {})) {
      out.push(device);
    }
  }
  return out;
}

// Frontend mirror of ic_modules connections.customerHasEnabledConnection (which the CRA bundle
// cannot import from the backend modules dir). `customerSettings` is the redux settings payload,
// whose telematicsConnections registry is the SOURCE OF TRUTH for which provider connections exist.
// The deprecated general.deviceOEMProviders list is only a backup for the registry, so it must not
// be the membership test -- connections added/enabled via the UI update the registry, not that list.
const TELEMATICS_ID_KEY = {geotab: 'geotabName', samsara: 'orgId', johndeere: 'orgId', pellenc: 'orgId'};

// True if the customer has >=1 ENABLED connection for `provider`. Mirrors resolveEnabledConnections:
// an entry counts when it is not archived (enabled !== false) and carries a non-empty id key
// (a bare-string legacy entry is its own id key).
export function customerHasEnabledConnection(customerSettings, provider) {
  const idKey = TELEMATICS_ID_KEY[provider];
  if (!idKey) {
    return false;
  }
  const entries = (customerSettings?.telematicsConnections || {})[provider] || [];
  return entries.some((entry) => {
    if (typeof entry === 'string') {
      return entry.trim() !== '';
    }
    if (!entry || entry.enabled === false) {
      return false;
    }
    return !!entry[idKey] && String(entry[idKey]).trim() !== '';
  });
}

// Number of DISTINCT providers (geotab/samsara/johndeere/pellenc) that have >=1 enabled connection. Used to
// decide whether a provider column is worth showing (only meaningful with mixed providers).
export function countEnabledConnectionProviders(customerSettings) {
  return ['geotab', 'samsara', 'johndeere', 'pellenc'].filter((p) => {
    return customerHasEnabledConnection(customerSettings, p);
  }).length;
}

// True if a provider has any active devices across all connections.
export function providerHasActiveDevices(activeDevices, provider) {
  const byConn = (activeDevices && activeDevices[provider]) || {};
  return Object.values(byConn).some((connMap) => {
    return connMap && Object.keys(connMap).length > 0;
  });
}

export function getDeviceSerialDisplay(vehicle, activeDevices, vehicleDict) {
  const connectionid = vehicle?.connectionid;
  const vehDeviceId = vehicle?.device?.id;
  const geotabActive = getActiveDevice(activeDevices, 'geotab', connectionid, vehDeviceId);
  const samsaraActive = getActiveDevice(activeDevices, 'samsara', connectionid, vehDeviceId);
  const johndeereActive = getActiveDevice(activeDevices, 'johndeere', connectionid, vehDeviceId);
  const pellencActive = getActiveDevice(activeDevices, 'pellenc', connectionid, vehDeviceId);

  let deviceSerial = '';
  let provider = '';
  if (geotabActive || vehicle?.provider == 'geotab') {
    deviceSerial =
      vehicle?.device?.serialNumber != undefined && vehicle?.device?.serialNumber != '000-000-0000'
        ? vehicle?.device?.serialNumber
        : '';
    provider = 'geotab';
  }

  // captures samsara case
  if (samsaraActive) {
    if (vehicle?.device?.installedGateway?.serial) {
      deviceSerial = vehicle?.device?.installedGateway?.serial;
    } else if (vehicle?.device?.gateway?.serial) {
      deviceSerial = vehicle?.device?.gateway?.serial;
    }
    provider = 'samsara';
  }

  // captures johndeere case
  if (johndeereActive) {
    deviceSerial = vehicle?.device?.serialNumber;
    provider = 'johndeere';
  }

  // captures pellenc case
  if (pellencActive) {
    deviceSerial = vehicle?.device?.serialNumber;
    provider = 'pellenc';
  }

  let beWhereSerial = undefined;
  if (vehicle?.device?.serialNumber && geotabActive) {
    beWhereSerial = extractBeWhereSerialFromGeotabComment(geotabActive?.comment);
  }

  const vehicleHasActiveGeotabDevice = !!geotabActive;
  const vehicleHasActiveSamsaraDevice = !!samsaraActive;
  const vehicleHasActiveJohndeereDevice = !!johndeereActive;
  const vehicleHasActivePellencDevice = !!pellencActive;
  const hasValidSerial = deviceSerial != undefined && deviceSerial != '' && deviceSerial != '000-000-0000';

  let gpsDeviceSerialDisplay = 'Not Installed';
  if (provider == 'geotab' && hasValidSerial) {
    if (vehicleHasActiveGeotabDevice && vehicle?.device?.serialNumber == geotabActive.serialNumber) {
      gpsDeviceSerialDisplay = beWhereSerial ?? deviceSerial;
    } else {
      gpsDeviceSerialDisplay = 'Not Synced';
    }
  } else if (vehicleHasActiveSamsaraDevice) {
    if (typeof vehicleDict?.[vehicle.serialNumber]?.device?.installedGateway?.serial !== 'undefined') {
      gpsDeviceSerialDisplay = vehicleDict[vehicle.serialNumber].device.installedGateway.serial;
    } else if (typeof vehicleDict?.[vehicle.serialNumber]?.device?.gateway?.serial !== 'undefined') {
      gpsDeviceSerialDisplay = vehicleDict[vehicle.serialNumber].device.gateway.serial;
    }
  } else if (vehicleHasActiveJohndeereDevice || vehicleHasActivePellencDevice) {
    gpsDeviceSerialDisplay = vehicle?.device?.serialNumber;
  } else {
    if (typeof vehicleDict?.[vehicle.serialNumber]?.device?.serial !== 'undefined') {
      gpsDeviceSerialDisplay = vehicleDict[vehicle.serialNumber].device.serial;
    }
  }

  return gpsDeviceSerialDisplay;
}

/**
 * Generic, used by most other parts of our platform
 *
 * @param {Object} customerSettings
 * @return {Array}
 */
export function getPreselectDateRanges(customerSettings) {
  // Get startDate for current shift in customer timezone
  const now = DateTime.local({zone: customerSettings.general.timeZone});

  const todayShiftStartDate = DateTime.local({
    zone: customerSettings.general.timeZone,
  }).set({
    hour: customerSettings.general.hourOffSet,
    minute: customerSettings.general.minuteOffSet,
    second: 0,
    millisecond: 0,
  });

  const weekShiftStartDate = DateTime.local({
    zone: customerSettings.general.timeZone,
  })
    .startOf('week')
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    });

  const monthShiftStartDate = DateTime.local({
    zone: customerSettings.general.timeZone,
  })
    .startOf('month')
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    });

  let nowInLastDayShift = false;
  let nowInLastWeekShift = false;
  let nowInLastMonthShift = false;

  // Shift started previous interval (day/week/month) if startDate is greater than now
  if (todayShiftStartDate > now) {
    nowInLastDayShift = true;
  }
  if (weekShiftStartDate > now) {
    nowInLastWeekShift = true;
  }
  if (monthShiftStartDate > now) {
    nowInLastMonthShift = true;
  }

  // Anchor on the customer's calendar day: a date-only ISO string would otherwise parse in the browser zone.
  const todayStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .plus({days: nowInLastDayShift ? -1 : 0})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    });

  const todayEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    })
    .plus({millisecond: -1, days: nowInLastDayShift ? 0 : 1});

  const currWeekStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .startOf('week')
    .plus({weeks: nowInLastWeekShift ? -1 : 0})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    });

  const currWeekEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .startOf('week')
    .plus({weeks: nowInLastWeekShift ? 0 : 1})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    })
    .plus({millisecond: -1});

  const currMonthStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .startOf('month')
    .plus({months: nowInLastMonthShift ? -1 : 0})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    });

  const currMonthEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone})
    .startOf('month')
    .plus({months: nowInLastMonthShift ? 0 : 1})
    .set({
      hour: customerSettings.general.hourOffSet,
      minute: customerSettings.general.minuteOffSet,
      second: 0,
      millisecond: 0,
    })
    .plus({millisecond: -1});

  const dateRanges = {
    'Today': [moment(todayStartShift.toISO()), moment(todayEndShift.toISO())],
    'Yesterday': [
      moment(todayStartShift.toISO()).subtract(1, 'days'),
      moment(todayEndShift.toISO()).subtract(1, 'days'),
    ],
    'This Week': [moment(currWeekStartShift.toISO()), moment(currWeekEndShift.toISO())],
    'Last Week': [
      moment(currWeekStartShift.toISO()).subtract(1, 'weeks'),
      moment(currWeekEndShift.toISO()).subtract(1, 'weeks'),
    ],
    'This Month': [moment(currMonthStartShift.toISO()), moment(currMonthEndShift.toISO())],
    'Last Month': [
      moment(currMonthStartShift.toISO()).subtract(1, 'months'),
      moment(currMonthEndShift.toISO()).subtract(1, 'months'),
    ],
  };

  return dateRanges;
}

/**
 * Specific to statsview
 *
 * @param {Object} customerSettings
 * @return {Array}
 */
export function getPreselectDateRangesStatsview(customerSettings) {
  // Get startDate for current shift in customer timezone
  const now = DateTime.local({zone: customerSettings.general.timeZone});

  // Anchor on the customer's calendar day: a date-only ISO string would otherwise parse in the browser zone.
  const currWeekStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).startOf(
    'week'
  );
  const currWeekEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).endOf('week');

  const currMonthStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).startOf(
    'month'
  );
  const currMonthEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).endOf('month');

  const currQuarterStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).startOf(
    'quarter'
  );
  const currQuarterEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).endOf(
    'quarter'
  );

  const currYearStartShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).startOf(
    'year'
  );
  const currYearEndShift = DateTime.fromISO(now.toISODate(), {zone: customerSettings.general.timeZone}).endOf('year');

  const dateRanges = {
    'This Week': [moment(currWeekStartShift.toISO()), moment(currWeekEndShift.toISO())],
    'Last Week': [
      moment(currWeekStartShift.toISO()).subtract(1, 'weeks'),
      moment(currWeekEndShift.toISO()).subtract(1, 'weeks'),
    ],
    'This Month': [moment(currMonthStartShift.toISO()), moment(currMonthEndShift.toISO())],
    'Last Month': [
      moment(currMonthStartShift.toISO()).subtract(1, 'months'),
      moment(currMonthEndShift.toISO()).subtract(1, 'months'),
    ],
    'This Quarter': [moment(currQuarterStartShift.toISO()), moment(currQuarterEndShift.toISO())],
    'Last Quarter': [
      moment(currQuarterStartShift.toISO()).subtract(3, 'months'),
      moment(currQuarterEndShift.toISO()).subtract(3, 'months'),
    ],
    'This Year': [moment(currYearStartShift.toISO()), moment(currYearEndShift.toISO())],
    'Last Year': [
      moment(currYearStartShift.toISO()).subtract(1, 'years'),
      moment(currYearEndShift.toISO()).subtract(1, 'years'),
    ],
  };

  return dateRanges;
}
